import { Box, Button, Card, Chip, IconButton, Skeleton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined'
import { useEffect, useRef, useState } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import sectionApi from '../../api/sectionApi'
import taskApi from '../../api/taskApi'
import TaskPanel from './TaskPanel'
import ConfirmDialog from './ConfirmDialog'
import { useToast } from './ToastProvider'
import { errorMessage } from '../../utils/errors'
import { moveBetweenLists, moveWithinList } from '../../utils/dnd'
import { tokens } from '../../theme'

const COLUMN_WIDTH = 300

const plainText = (html) =>
  (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim()

const ColumnSkeleton = () => (
  <Box
    sx={{
      width: COLUMN_WIDTH,
      flexShrink: 0,
      p: 1.5,
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'rgba(19, 24, 32, 0.6)'
    }}
  >
    <Skeleton variant='rounded' height={20} width='60%' sx={{ mb: 2 }} />
    <Skeleton variant='rounded' height={54} sx={{ mb: 1 }} />
    <Skeleton variant='rounded' height={54} sx={{ mb: 1 }} />
    <Skeleton variant='rounded' height={54} />
  </Box>
)

const Kanban = ({ data: sections, boardId, loading }) => {
  const toast = useToast()
  const [data, setData] = useState([])
  const [selectedTask, setSelectedTask] = useState(undefined)
  const [pendingSection, setPendingSection] = useState(null)
  const timers = useRef({})

  useEffect(() => {
    setData(Array.isArray(sections) ? sections : [])
  }, [sections])

  useEffect(() => {
    const currentTimers = timers.current
    return () => Object.values(currentTimers).forEach(clearTimeout)
  }, [])

  const onDragEnd = async ({ source, destination }) => {
    if (!destination) return
    if (destination.droppableId === source.droppableId && destination.index === source.index) return

    const sourceColIndex = data.findIndex((section) => section.id === source.droppableId)
    const destinationColIndex = data.findIndex((section) => section.id === destination.droppableId)
    if (sourceColIndex === -1 || destinationColIndex === -1) return

    const sourceCol = data[sourceColIndex]
    const destinationCol = data[destinationColIndex]
    const sameSection = sourceCol.id === destinationCol.id

    const moved = sameSection
      ? {
        source: null,
        destination: moveWithinList(sourceCol.tasks, source.index, destination.index)
      }
      : moveBetweenLists(sourceCol.tasks, destinationCol.tasks, source.index, destination.index)

    if (moved.destination === destinationCol.tasks) return

    const previous = data
    const next = [...data]
    if (sameSection) {
      next[sourceColIndex] = { ...sourceCol, tasks: moved.destination }
    } else {
      next[sourceColIndex] = { ...sourceCol, tasks: moved.source }
      next[destinationColIndex] = { ...destinationCol, tasks: moved.destination }
    }
    setData(next)

    try {
      await taskApi.updatePosition(boardId, {
        resourceList: moved.source || [],
        destinationList: moved.destination,
        resourceSectionId: sourceCol.id,
        destinationSectionId: destinationCol.id
      })
    } catch (err) {
      setData(previous)
      toast.error(errorMessage(err, 'Could not move that task.'))
    }
  }

  const createSection = async () => {
    try {
      const section = await sectionApi.create(boardId)
      setData((previous) => [...previous, section])
    } catch (err) {
      toast.error(errorMessage(err, 'Could not create the section.'))
    }
  }

  const deleteSection = async (sectionId) => {
    clearTimeout(timers.current[sectionId])
    try {
      await sectionApi.delete(boardId, sectionId)
      setData((previous) => previous.filter((section) => section.id !== sectionId))
      setPendingSection(null)
    } catch (err) {
      toast.error(errorMessage(err, 'Could not delete the section.'))
    }
  }

  const updateSectionTitle = (event, sectionId) => {
    const newTitle = event.target.value
    setData((previous) => previous.map((section) => (
      section.id === sectionId ? { ...section, title: newTitle } : section
    )))

    clearTimeout(timers.current[sectionId])
    timers.current[sectionId] = setTimeout(async () => {
      try {
        await sectionApi.update(boardId, sectionId, { title: newTitle })
      } catch (err) {
        toast.error(errorMessage(err, 'Could not rename the section.'))
      }
    }, 500)
  }

  const createTask = async (sectionId) => {
    try {
      const task = await taskApi.create(boardId, { sectionId })
      setData((previous) => previous.map((section) => (
        section.id === sectionId
          ? { ...section, tasks: [task, ...section.tasks] }
          : section
      )))
    } catch (err) {
      toast.error(errorMessage(err, 'Could not create the task.'))
    }
  }

  const onUpdateTask = (task) => {
    setData((previous) => previous.map((section) => ({
      ...section,
      tasks: section.tasks.map((item) => (item.id === task.id ? { ...task } : item))
    })))
  }

  const onDeleteTask = (task) => {
    setData((previous) => previous.map((section) => ({
      ...section,
      tasks: section.tasks.filter((item) => item.id !== task.id)
    })))
    setSelectedTask(undefined)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 2, md: 4 },
          pb: 1.5,
          flexShrink: 0
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='overline' color='text.disabled'>
            Sections
          </Typography>
          {!loading && (
            <Chip
              size='small'
              label={data.length}
              sx={{ height: 20, backgroundColor: 'background.paper', color: 'text.secondary' }}
            />
          )}
        </Stack>
        <Button
          size='small'
          variant='outlined'
          startIcon={<AddOutlinedIcon sx={{ fontSize: 18 }} />}
          onClick={createSection}
        >
          Add section
        </Button>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
          px: { xs: 2, md: 4 },
          pb: 2
        }}
      >
        {loading ? (
          <Stack direction='row' spacing={2} sx={{ height: '100%' }}>
            <ColumnSkeleton />
            <ColumnSkeleton />
            <ColumnSkeleton />
          </Stack>
        ) : data.length === 0 ? (
          <Box
            sx={{
              height: '100%',
              display: 'grid',
              placeItems: 'center',
              borderRadius: 4,
              border: '1px dashed',
              borderColor: 'divider',
              backgroundColor: 'rgba(19, 24, 32, 0.5)',
              px: 3,
              py: 6,
              textAlign: 'center'
            }}
          >
            <Box sx={{ maxWidth: 360 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  mx: 'auto',
                  mb: 2,
                  borderRadius: 2.5,
                  display: 'grid',
                  placeItems: 'center',
                  color: 'primary.main',
                  backgroundColor: tokens.accentSoft
                }}
              >
                <ViewKanbanOutlinedIcon fontSize='small' />
              </Box>
              <Typography variant='h5' sx={{ mb: 1 }}>
                This board is empty
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                Sections group your tasks. Add one for each stage of the work, then drop
                tasks between them.
              </Typography>
              <Button
                variant='contained'
                size='small'
                startIcon={<AddOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={createSection}
              >
                Add the first section
              </Button>
            </Box>
          </Box>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                height: '100%',
                minWidth: 'min-content'
              }}
            >
              {data.map((section) => (
                <Box
                  key={section.id}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: COLUMN_WIDTH,
                    flexShrink: 0,
                    maxHeight: '100%',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'rgba(19, 24, 32, 0.72)'
                  }}
                >
                  <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
                    <Stack direction='row' alignItems='center' spacing={0.5}>
                      <TextField
                        value={section.title}
                        onChange={(event) => updateSectionTitle(event, section.id)}
                        placeholder='Untitled section'
                        variant='outlined'
                        fullWidth
                        aria-label='Section title'
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'transparent',
                            paddingInline: 0.5,
                            '& fieldset': { border: 'none' },
                            fontSize: '0.875rem',
                            fontWeight: 600
                          },
                          '& .MuiOutlinedInput-input': { padding: '2px 0' }
                        }}
                      />
                      <Tooltip title='Add task'>
                        <IconButton
                          size='small'
                          onClick={() => createTask(section.id)}
                          aria-label={`Add task to ${section.title || 'section'}`}
                        >
                          <AddOutlinedIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Delete section'>
                        <IconButton
                          size='small'
                          onClick={() => setPendingSection(section)}
                          aria-label={`Delete section ${section.title || ''}`}
                        >
                          <DeleteOutlineRoundedIcon fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Box>

                  <Droppable droppableId={section.id}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          flexGrow: 1,
                          minHeight: 60,
                          overflowY: 'auto',
                          px: 1.5,
                          py: 1,
                          backgroundColor: snapshot.isDraggingOver ? 'rgba(47, 196, 155, 0.06)' : 'transparent',
                          transition: 'background-color 160ms ease'
                        }}
                      >
                        {section.tasks.length === 0 && !snapshot.isDraggingOver && (
                          <Box
                            sx={{
                              borderRadius: 2,
                              border: '1px dashed',
                              borderColor: 'divider',
                              px: 1.5,
                              py: 2,
                              textAlign: 'center'
                            }}
                          >
                            <Typography variant='caption' color='text.disabled'>
                              Drop a task here
                            </Typography>
                          </Box>
                        )}

                        {section.tasks.map((task, index) => {
                          const preview = plainText(task.content)
                          return (
                            <Draggable key={task.id} draggableId={task.id} index={index}>
                              {(dragProvided, dragSnapshot) => (
                                <Card
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  onClick={() => setSelectedTask(task)}
                                  sx={{
                                    mb: 1,
                                    p: 1.5,
                                    cursor: 'grab',
                                    backgroundColor: dragSnapshot.isDragging ? 'background.paper' : tokens.raised,
                                    borderColor: dragSnapshot.isDragging ? 'primary.main' : 'divider',
                                    boxShadow: dragSnapshot.isDragging ? `0 12px 28px ${tokens.shadow}` : 'none',
                                    transition: 'border-color 160ms ease, background-color 160ms ease',
                                    '&:hover': {
                                      backgroundColor: tokens.hover,
                                      borderColor: tokens.borderStrong
                                    }
                                  }}
                                >
                                  <Typography
                                    variant='body2'
                                    fontWeight={600}
                                    sx={{
                                      color: task.title ? 'text.primary' : 'text.disabled',
                                      overflowWrap: 'anywhere'
                                    }}
                                  >
                                    {task.title || 'Untitled'}
                                  </Typography>
                                  {preview && (
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        mt: 0.5
                                      }}
                                    >
                                      {preview}
                                    </Typography>
                                  )}
                                </Card>
                              )}
                            </Draggable>
                          )
                        })}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>

                  <Button
                    onClick={() => createTask(section.id)}
                    startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      justifyContent: 'flex-start',
                      mx: 1,
                      mb: 1,
                      px: 1,
                      color: 'text.secondary',
                      fontSize: '0.8125rem'
                    }}
                    size='small'
                  >
                    Add task
                  </Button>
                </Box>
              ))}
            </Box>
          </DragDropContext>
        )}
      </Box>

      <TaskPanel
        task={selectedTask}
        boardId={boardId}
        onClose={() => setSelectedTask(undefined)}
        onUpdate={onUpdateTask}
        onDelete={onDeleteTask}
      />

      <ConfirmDialog
        open={Boolean(pendingSection)}
        title='Delete this section?'
        description='Every task inside this section will be removed. This cannot be undone.'
        confirmLabel='Delete section'
        onCancel={() => setPendingSection(null)}
        onConfirm={() => deleteSection(pendingSection.id)}
      />
    </>
  )
}

export default Kanban
